import React, { useEffect } from 'react';
import { Flex, Card, Empty, Spin, Popconfirm, Typography, Button } from 'antd';
import { DeleteOutlined, PlusOutlined, RightOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import GreenLightButton from '../../components/UI/Buttons/GreenLightButton';
import MyText from '../../components/UI/Text/MyText';
import { clrs } from '../../constants/colors';
import { fetchJobs, createJob, removeJob } from '../../store/slices/lspivSlice';
import classes from './lspiv.module.css';

const { Title, Text } = Typography;

const statusClass = (status) => {
    if (status === 'running') return classes.statusRunning;
    if (status === 'error') return classes.statusError;
    if (status === 'idle') return classes.statusIdle;
    return classes.statusCreated;
};

const LSPIVHomePage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { jobs, isLoading } = useSelector((s) => s.lspiv);

    useEffect(() => {
        dispatch(fetchJobs());
    }, [dispatch]);

    const handleCreate = async () => {
        const action = await dispatch(createJob());
        if (action?.payload?.job_id) {
            navigate(`/lspiv/${action.payload.job_id}`);
        }
    };

    const handleDelete = (jobId, e) => {
        e.stopPropagation();
        dispatch(removeJob(jobId));
    };

    return (
        <div>
            <Header />
            <div className={classes.page}>
                <div className={classes.wrapper}>
                    <h1 className={classes.title}>Анализ скорости и расхода воды (LSPIV)</h1>
                    <p className={classes.subtitle}>
                        Large-Scale Particle Image Velocimetry — измерение скорости воды и расхода реки
                        по видеозаписи. Загрузите видео, настройте ректификацию и сечения, получите отчёт.
                    </p>

                    <Card className={classes.card} bodyStyle={{ padding: '24px' }}>
                        <Flex justify="space-between" align="center" wrap="wrap" gap={16}>
                            <Flex vertical gap={4}>
                                <Title level={4} style={{ margin: 0, color: clrs.textColor }}>
                                    Новый анализ
                                </Title>
                                <Text type="secondary">
                                    Создать новое задание LSPIV и начать wizard
                                </Text>
                            </Flex>
                            <GreenLightButton
                                onClick={handleCreate}
                                style={{ fontSize: '1rem', padding: '10px 24px' }}
                            >
                                <PlusOutlined /> Создать задание
                            </GreenLightButton>
                        </Flex>
                    </Card>

                    <Card
                        className={classes.card}
                        title={<Title level={5} style={{ margin: 0 }}>Мои задания</Title>}
                        bodyStyle={{ padding: '16px' }}
                    >
                        {isLoading && (
                            <Flex align="center" justify="center" style={{ padding: '40px 0' }}>
                                <Spin />
                            </Flex>
                        )}
                        {!isLoading && jobs.length === 0 && (
                            <Empty description="Заданий пока нет" />
                        )}
                        {!isLoading && jobs.length > 0 && (
                            <Flex vertical gap={12}>
                                {jobs.map((job) => (
                                    <Card
                                        key={job.id}
                                        size="small"
                                        className={classes.jobCard}
                                        onClick={() => navigate(`/lspiv/${job.id}`)}
                                        bodyStyle={{ padding: '12px 16px' }}
                                    >
                                        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
                                            <Flex vertical gap={4}>
                                                <Text strong style={{ fontSize: '1rem' }}>
                                                    Job #{job.id}
                                                </Text>
                                                <Flex gap={8} align="center">
                                                    <span className={`${classes.statusBadge} ${statusClass(job.status)}`}>
                                                        {job.status || 'created'}
                                                    </span>
                                                    {job.step && (
                                                        <Text type="secondary" style={{ fontSize: '0.85rem' }}>
                                                            {job.step}
                                                        </Text>
                                                    )}
                                                </Flex>
                                                {job.updated_at && (
                                                    <Text type="secondary" style={{ fontSize: '0.8rem' }}>
                                                        {new Date(job.updated_at).toLocaleString()}
                                                    </Text>
                                                )}
                                            </Flex>
                                            <Flex gap={8}>
                                                <Popconfirm
                                                    title="Удалить задание?"
                                                    description="Все файлы будут удалены безвозвратно"
                                                    onConfirm={(e) => handleDelete(job.id, e)}
                                                    onCancel={(e) => e.stopPropagation()}
                                                    okText="Удалить"
                                                    cancelText="Отмена"
                                                >
                                                    <Button
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </Popconfirm>
                                                <Button icon={<RightOutlined />} type="primary" ghost>
                                                    Открыть
                                                </Button>
                                            </Flex>
                                        </Flex>
                                    </Card>
                                ))}
                            </Flex>
                        )}
                    </Card>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default LSPIVHomePage;
